import {
    domScaleForContainer,
    positionOverlayInReferenceResolution,
} from '../nteOverlayPositioning';
import type { OverlayBounds, Resolution } from '../nteOverlayPositioning';

// Styles mirrored onto each overlay so it wraps like the raster. Length-valued ones are in
// reference pixels (scaled to screen); the rest copied verbatim.
const SCALED_STYLE_PROPS = [
    'fontSize',
    'lineHeight',
    'letterSpacing',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
];
const COPIED_STYLE_PROPS = [
    'fontFamily',
    'fontWeight',
    'fontStyle',
    'color',
    'textAlign',
    'textTransform',
    'whiteSpace',
];

/** The computed style properties an editable region must supply for its overlay to match the raster. */
export const EDITABLE_OVERLAY_STYLE_PROPS = [...COPIED_STYLE_PROPS, ...SCALED_STYLE_PROPS];

export interface EditableRegionDescriptor {
    id: string;
    text: string;
    /** Bounds within the asset's own reference space (0..assetWidth, 0..assetHeight). */
    bounds: OverlayBounds;
    /** Computed style values (see `EDITABLE_OVERLAY_STYLE_PROPS`). */
    style: Record<string, string>;
}

export interface HtmlTextEditConfig {
    /** The asset's box in scene reference coordinates. */
    assetBounds: OverlayBounds;
    /** The scene/video reference resolution. */
    referenceResolution: Resolution;
    regions: EditableRegionDescriptor[];
    commit: (id: string, value: string) => void;
}

function scalePxValue(value: string, scale: number): string {
    const match = /^(-?[\d.]+)px$/.exec(value);
    return match ? `${parseFloat(match[1]) * scale}px` : value;
}

interface Overlay {
    id: string;
    element: HTMLElement;
    original: string;
}

/**
 * Injects one `contentEditable` overlay per editable region into an editor-provided container — the
 * visible copy of the text while the raster hides the glyphs. Overlays are positioned as a percentage
 * of the reference resolution (so the editor's transform maps them to screen, as TextAsset does) with
 * the composition's computed styles mirrored so wrapping matches. Edits commit on blur.
 */
export class HtmlTextEditManager {
    #domContainer: HTMLElement;
    #container: HTMLElement | null = null;
    #overlays: Overlay[] = [];
    #commit: (id: string, value: string) => void;

    constructor(domContainer: HTMLElement, config: HtmlTextEditConfig) {
        this.#domContainer = domContainer;
        this.#commit = config.commit;
        this.#build(config);
    }

    #build(config: HtmlTextEditConfig): void {
        const doc = this.#domContainer.ownerDocument;
        const container = doc.createElement('div');
        container.className = 'l5-html-nte-container';
        container.style.position = 'absolute';
        positionOverlayInReferenceResolution(
            container,
            config.assetBounds,
            config.referenceResolution,
        );
        this.#domContainer.appendChild(container);
        this.#container = container;

        // Regions are positioned within the asset box, so the asset's own size is the resolution.
        const assetSize: Resolution = {
            width: config.assetBounds.width,
            height: config.assetBounds.height,
        };
        const domScale = domScaleForContainer(this.#domContainer, config.referenceResolution);

        for (const region of config.regions) {
            const overlay = doc.createElement('div');
            overlay.contentEditable = 'true';
            overlay.spellcheck = false;
            overlay.dataset.l5EditableId = region.id;
            overlay.style.position = 'absolute';
            overlay.style.boxSizing = 'border-box';
            overlay.style.outline = 'none';
            // The editor disables text selection on its controls layer; the overlay must opt back
            // in (it inherits `user-select: none` otherwise) so the user can select/edit the text.
            overlay.style.userSelect = 'text';
            overlay.style.setProperty('-webkit-user-select', 'text');
            overlay.style.cursor = 'text';
            overlay.style.pointerEvents = 'auto';
            positionOverlayInReferenceResolution(overlay, region.bounds, assetSize);

            const style = overlay.style as unknown as Record<string, string>;
            for (const prop of COPIED_STYLE_PROPS) {
                style[prop] = region.style[prop] ?? '';
            }
            for (const prop of SCALED_STYLE_PROPS) {
                if (region.style[prop]) {
                    style[prop] = scalePxValue(region.style[prop], domScale);
                }
            }
            overlay.textContent = region.text;

            const entry: Overlay = { id: region.id, element: overlay, original: region.text };
            overlay.addEventListener('blur', () => this.#commitOverlay(entry));
            container.appendChild(overlay);
            this.#overlays.push(entry);
        }
    }

    #commitOverlay(entry: Overlay): void {
        const text = entry.element.textContent ?? '';
        if (!entry.id || text === entry.original) {
            return;
        }
        try {
            this.#commit(entry.id, text);
            entry.original = text;
        } catch {
            // The node is no longer targetable in `#html` (an id-less region, or an agent revision
            // removed it); drop the edit rather than let the throw escape the blur/commit handler.
        }
    }

    /** Focus the overlay for a given editable id (e.g. the region the user clicked). */
    focus(id: string): void {
        this.#overlays.find((overlay) => overlay.id === id)?.element.focus();
    }

    /** Commit any pending (changed) overlay before teardown. */
    commitAll(): void {
        for (const entry of this.#overlays) {
            this.#commitOverlay(entry);
        }
    }

    /** Remove the injected overlay DOM. Separate from commitAll so the caller can re-raster the
     *  committed text (with glyphs restored) before the overlays disappear — avoids a flash. */
    removeOverlays(): void {
        this.#container?.parentNode?.removeChild(this.#container);
        this.#container = null;
        this.#overlays = [];
    }

    destroy(): void {
        this.commitAll();
        this.removeOverlays();
    }
}
